import {
  ClientInvoiceCommentRequest,
  ClientInvoiceManualRequest,
} from "../../models/requestModels/ClientInvoiceRequests";
import {
  ApiResponse,
  CiEditListReponse,
  ClientInvoiceAllLceResponse,
  ClientInvoiceAllOtaResponse,
  ClientInvoiceCommentUIResponse,
  ClientInvoiceEditDetailUIResponse,
  ClientInvoiceLceResponse,
  ClientInvoiceListUIResponse,
  ClientInvoiceOtaResponse,
  ClientInvoiceRouteListResponse,
  ClientInvoiceRouteUIResponse,
  ClientInvoiceUIResponse,
  DateRangeInvoiceEditsUIResponse,
  StoreForClientInvoiceUIResponse,
  SubmittedClientInvoiceListUIResponse,
  TripAndDateRangeDataForSelectedStoresUIResponse,
  ValidateInvoiceDetailsResponse,
} from "../../models/ApiResponse";
import { axiosRequests } from "../axiosRequest";
import { ClientInvoiceListDataArgs } from "../../../pages/clients/invoices/ClientInvoiceConstants";
import { EditClientInvoiceRequest } from "../../models/requestModels/ClientInvoiceEditRequest";
import { Dayjs } from "dayjs";
import {
  ClientInvoiceLce,
  ClientInvoiceOta,
} from "../../models/responseModels/ClientInvoiceResponses";

const INVOICE_MANAGER_ENDPOINT = "fdc-invoice";
export const ClientInvoiceAgent = {
  getClientInvoiceList: (clientId: number, args: ClientInvoiceListDataArgs) =>
    axiosRequests.getWithQueryParam<ClientInvoiceListUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/client/${clientId}`,
      {
        params: {
          pageIndex: args.pageIndex,
          pageSize: args.pageSize,
        },
      }
    ),
  getClientSubmittedInvoiceList: (clientId: number, args: ClientInvoiceListDataArgs) =>
    axiosRequests.getWithQueryParam<SubmittedClientInvoiceListUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/client/submitted/${clientId}`,
      {
        params: {
          pageIndex: args.pageIndex,
          pageSize: args.pageSize,
        },
      }
    ),
  getClientInvoice: (id: number) =>
    axiosRequests.get<ClientInvoiceUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/${id}`
    ),
  fetchViewClientInvoiceFile: (invoicePkId: number, invoiceStatus: string) =>
    axiosRequests.getWithQueryParamAndReturnBlob<Blob>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/${invoicePkId}/${invoiceStatus}/download-file`,
      {
        responseType: "arraybuffer",
        headers: {
          "Content-Type": "application/pdf",
          Accept: "application/pdf",
        },
      }
    ),
  fetchDownloadClientInvoiceFile: (invoicePkId: number, invoiceStatus: string) =>
    axiosRequests.getWithQueryParamAndReturnBlob<Blob>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/${invoicePkId}/${invoiceStatus}/download-file/csv`,
      {
        responseType: "arraybuffer",
        headers: {
          "Content-Type": "application/pdf",
          Accept: "application/pdf",
        },
      }
    ),
  manuallyGenerateClientInvoice: (
    manualGenerateRequest: ClientInvoiceManualRequest
  ) =>
    axiosRequests.postWithQueryParamAndReturnBlob<Blob>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/generate-manual-client-invoice`,
      manualGenerateRequest,
      {
        responseType: "arraybuffer",
        headers: {
          Accept: "application/pdf",
        },
      }
    ),
  getRoutes: (
    storeIds: string,
    startDate: string | null,
    endDate: string | null
  ) =>
    axiosRequests.getWithQueryParam<ClientInvoiceRouteListResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/routes/fetch-routes-by-stores-and-date-range`,
      {
        params: {
          storeIds: storeIds,
          startDate: startDate,
          endDate: endDate,
        },
      }
    ),
  getRouteByTripId: (tripId: string) =>
    axiosRequests.getWithQueryParam<ClientInvoiceRouteUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/routes/fetch-route-by-trip-id`,
      {
        params: {
          tripId: tripId,
        },
      }
    ),
  getGenericRouteByTripId: (invoiceId: number, tripId: string) =>
    axiosRequests.getWithQueryParam<ClientInvoiceRouteUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/routes/fetch-generic-route-by-trip-id`,
      {
        params: {
          invoiceId: invoiceId,
          tripId: tripId,
        },
      }
    ),
  editClientInvoice: (
    invoiceId: number,
    clientInvoice: EditClientInvoiceRequest
  ) =>
    axiosRequests.put<CiEditListReponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/${invoiceId}`,
      clientInvoice
    ),
  getEditData: (
    clientInvoiceId: string,
    storeIds: string,
    startDate: string | null,
    endDate: string | null,
    tripId: string | null,
    ratecardId: number | null
  ) =>
    axiosRequests.getWithQueryParam<ClientInvoiceEditDetailUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/get-edit`,
      {
        params: {
          clientInvoiceId: clientInvoiceId,
          storeIds: storeIds,
          startDate: startDate,
          endDate: endDate,
          tripId: tripId,
          ratecard: ratecardId,
        },
      }
    ),
  getEditList: (clientInvoiceId: string) =>
    axiosRequests.get<CiEditListReponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/fetch-all-edits/${clientInvoiceId}`
    ),
  getEditDataById: (clientInvoiceEditId: number) =>
    axiosRequests.get<ClientInvoiceEditDetailUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/get-edit-by-id/${clientInvoiceEditId}`
    ),

  changeInvoiceStatus: (
    invoiceId: number,
    invoiceStatus: string,
    timestamp: Dayjs | null = null,
    submitType?: string
  ) =>
    axiosRequests.put<ClientInvoiceUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/change-invoice-status-by-id/${invoiceId}`,
      { invoiceStatus: invoiceStatus, timestamp: timestamp, ...(submitType && { submitType }) }
    ),

  getStoresForClientInvoice: (clientInvoiceId: string) =>
    axiosRequests.getWithQueryParam<StoreForClientInvoiceUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/get-stores-for-client-invoice`,
      {
        params: {
          clientInvoiceId: clientInvoiceId,
        },
      }
    ),

  getTripAndDateRangeForSelectedStores: (
    clientInvoiceId: string,
    storeIds: string
  ) =>
    axiosRequests.getWithQueryParam<TripAndDateRangeDataForSelectedStoresUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/get-trip-and-date-range-for-selected-stores`,
      {
        params: {
          clientInvoiceId: clientInvoiceId,
          storeIds: storeIds,
        },
      }
    ),
  getClientInvoiceComments: (invoicePkId: number) =>
    axiosRequests.getWithQueryParam<ClientInvoiceCommentUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/all-comments`,
      {
        params: {
          invoicePkId: invoicePkId,
        },
      }
    ),
  addClientInvoiceComment: (commentRequest: ClientInvoiceCommentRequest) =>
    axiosRequests.post<ClientInvoiceCommentUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/add-comment`,
      commentRequest
    ),
  editClientInvoiceComment: (commentRequest: ClientInvoiceCommentRequest) =>
    axiosRequests.post<ClientInvoiceCommentUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/edit-comment`,
      commentRequest
    ),
  deleteClientInvoiceComment: (commentId: number) =>
    axiosRequests.deleteWithQueryParam<ClientInvoiceCommentUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/delete-comment`,
      {
        params: {
          commentId: commentId,
        },
      }
    ),
  getDateRangeEdits: (clientInvoiceId: string, storeIds: string) =>
    axiosRequests.getWithQueryParam<DateRangeInvoiceEditsUIResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/fetch-date-range-edits`,
      {
        params: {
          clientInvoiceId: clientInvoiceId,
          storeIds: storeIds,
        },
      }
    ),
  deleteInvoiceEdit: (
    clientInvoiceId: string,
    invoiceEditIds: string,
    fdpInvoiceCreatedOn: string | null
  ) =>
    axiosRequests.deleteWithQueryParam<CiEditListReponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/delete-invoice-edit`,
      {
        params: {
          clientInvoiceId: clientInvoiceId,
          fdpInvoiceCreatedOn: fdpInvoiceCreatedOn,
          invoiceEditIds: invoiceEditIds, // this is string of comma-separated numbers like "1,2"
        },
      }
    ),
  addClientInvoiceOta: (invoiceOta: ClientInvoiceOta) =>
    axiosRequests.post<ClientInvoiceOtaResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoice/ota/new`,
      invoiceOta
    ),
  fetchClientInvoiceAllOtas: (invoiceId: number) =>
    axiosRequests.get<ClientInvoiceAllOtaResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoice/ota/all?invoiceId=${invoiceId}`
    ),
  deleteClientInvoiceOta: (otaId: number) =>
    axiosRequests.delete<ApiResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoice/ota/remove?otaId=${otaId}`
    ),
  addClientInvoiceLce: (invoiceOta: ClientInvoiceLce) =>
    axiosRequests.post<ClientInvoiceLceResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/late-cancel-edit/new`,
      invoiceOta
    ),
  fetchClientInvoiceAllLces: (invoiceId: number) =>
    axiosRequests.get<ClientInvoiceAllLceResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/late-cancel-edit/all?invoiceId=${invoiceId}`
    ),
  deleteClientInvoiceLce: (lceId: number) =>
    axiosRequests.delete<ApiResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/late-cancel-edit/remove?lceId=${lceId}`
    ),

  softDeleteInvoices: (
    invoiceList: string[],
    source: string
  ) => {
    axiosRequests.put<ApiResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/invoice/soft-delete?source=${source}`, invoiceList
    )
  },
  validateInvoiceDetailsSubmitInternal: (clientId: number, storeIds: string[], startDate: string, endDate: string) =>
    axiosRequests.post<ValidateInvoiceDetailsResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/validate/invoice-status/details`,
      { clientId, storeIds, startDate, endDate }
    ),
  validateInvoiceDetails: (clientId: number, storeIds: string[], startDate: string, endDate: string) =>
    axiosRequests.post<ValidateInvoiceDetailsResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/validate/invoice-details`,
      { clientId, storeIds, startDate, endDate }
    ),
  uploadManualSubmittedInvoice: (formData: FormData) =>
    axiosRequests.postWithFormData<ApiResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/client-invoice/manual-upload`,
      formData
    ),
  fetchDownloadMAnualUploadedClientInvoiceFile: (id: number) =>
    axiosRequests.getWithQueryParamAndReturnBlob<Blob>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/download-submitted-invoice/${id}`,
      {
        responseType: "arraybuffer",
        headers: {
          "Content-Type": "application/pdf",
          Accept: "application/pdf",
        },
      }
    ),
  softDeleteSubmittedInvoices: (submittedInvoices: { id: number; clientInvoiceId: string }[]) =>
    axiosRequests.put<ApiResponse>(
      `${INVOICE_MANAGER_ENDPOINT}/client-invoices/submitted-invoice/soft-delete`,
      submittedInvoices
    ),
};
